import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal, flushSync } from 'react-dom'
import { Button, Checkbox, Icon, Menu, MenuRow, MenuFlyout } from 'admination-react'
import { placeFlyout } from '@/core/lib/popoverPlace'
import type { TextOption } from '@/core/views/viewConfig'

const HOVER_OPEN_DELAY = 120
const HOVER_CLOSE_DELAY = 200

const FOREIGN_POPOVER_SELECTOR =
  '.date-picker-popover.is-open, .select-popover:not(.filter-menu-list):not(.filter-menu-flyout)'

const OWN_LIST_SELECTOR = '.filter-menu-list'

export type FilterMenuRow =

  | {
      kind: 'options'
      id: string
      label: string
      options: TextOption[]
      excluded: string[]
      onToggle: (key: string) => void
      count?: number

      sorted?: boolean
    }
  | {
      kind: 'radio'
      id: string
      label: string
      options: { value: string; label: string; count: number }[]
      current: string
      onSelect: (value: string) => void
    }
  | { kind: 'custom'; id: string; label: string; count?: number; render: () => ReactNode; stacked?: boolean }

function sortOptions(options: TextOption[]): TextOption[] {
  return [...options].sort((a, b) => a.label.localeCompare(b.label, 'de', { sensitivity: 'base' }))
}

function CheckboxOptionList({
  options,
  excluded,
  onToggle,
  sorted = true,
}: {
  options: TextOption[]
  excluded: string[]
  onToggle: (key: string) => void
  sorted?: boolean
}) {
  if (options.length === 0) return <div className="select-option is-disabled">Keine Optionen</div>
  const ordered = sorted ? sortOptions(options) : options
  return (
    <>
      {ordered.map((opt) => {
        const checked = !excluded.includes(opt.key)
        return (
          <Checkbox key={opt.key} className="select-option column-filter-option" round checked={checked} onChange={() => onToggle(opt.key)}>
            {opt.label}
          </Checkbox>
        )
      })}
    </>
  )
}

function RadioOptionList({
  options,
  current,
  onSelect,
}: {
  options: { value: string; label: string; count: number }[]
  current: string
  onSelect: (value: string) => void
}) {
  return (
    <>
      {options.map((opt) => (
        <Button
          key={opt.value}
          type="button"
          data-testid={`filter-option-${opt.value}`}
          variant="option" className={opt.value === current ? 'is-selected' : undefined}
          onClick={() => onSelect(opt.value)}
        >
          {opt.label}
          <span className="select-option-count">{opt.count}</span>
        </Button>
      ))}
    </>
  )
}

export function FilterMenu({ rows, active }: { rows: FilterMenuRow[]; active: boolean }) {
  const [open, setOpen] = useState(false)
  const [activeRow, setActiveRow] = useState<string | null>(null)

  const flyoutRef = useRef<HTMLDivElement>(null)
  const rowElRef = useRef<Record<string, HTMLButtonElement | null>>({})

  const [flyoutPos, setFlyoutPos] = useState<{ top: number; left: number; maxHeight?: number }>({
    top: 0,
    left: 0,
  })

  const hoverRowRef = useRef<string | null>(null)
  const hoverFlyoutRef = useRef(false)
  const openTimerRef = useRef<number | undefined>(undefined)
  const closeTimerRef = useRef<number | undefined>(undefined)

  const activeRowConfig = activeRow ? rows.find((r) => r.id === activeRow) : undefined

  const repositionFlyout = useCallback(() => {
    const row = activeRow ? rowElRef.current[activeRow] : null
    const pop = flyoutRef.current
    if (!row || !pop) return

    const scroller = pop.querySelector<HTMLElement>('.menu-scroll')!
    const placed = placeFlyout(row.getBoundingClientRect(), pop.getBoundingClientRect().width, scroller.scrollHeight + pop.clientHeight - scroller.clientHeight + 2)
    setFlyoutPos({ top: placed.top, left: placed.left, maxHeight: placed.maxHeight })
  }, [activeRow])

  useLayoutEffect(() => {
    if (activeRow) repositionFlyout()
  }, [activeRow, activeRowConfig, repositionFlyout])

  useEffect(() => {
    if (!open) setActiveRow(null)
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      if (document.querySelector(FOREIGN_POPOVER_SELECTOR)) return
      e.preventDefault()

      if (activeRow !== null) {
        setActiveRow(null)
        return
      }
      setOpen(false)
    }
    const onScroll = (e: Event) => {
      const t = e.target as HTMLElement
      if (flyoutRef.current?.contains(t)) return
      if (t.closest?.(OWN_LIST_SELECTOR)) return
      if (t.closest?.(FOREIGN_POPOVER_SELECTOR)) return
      setOpen(false)
    }
    document.addEventListener('keydown', onKey, true)
    document.addEventListener('scroll', onScroll, true)
    const onResize = () => flushSync(() => repositionFlyout())
    window.addEventListener('resize', onResize)
    return () => {
      document.removeEventListener('keydown', onKey, true)
      document.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onResize)
    }
  }, [open, activeRow, repositionFlyout])

  useEffect(
    () => () => {
      if (openTimerRef.current) window.clearTimeout(openTimerRef.current)
      if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current)
    },
    [],
  )

  function clearOpenTimer() {
    if (openTimerRef.current) {
      window.clearTimeout(openTimerRef.current)
      openTimerRef.current = undefined
    }
  }
  function clearCloseTimer() {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current)
      closeTimerRef.current = undefined
    }
  }
  function scheduleClose() {
    clearCloseTimer()
    closeTimerRef.current = window.setTimeout(() => {
      if (!hoverRowRef.current && !hoverFlyoutRef.current) setActiveRow(null)
    }, HOVER_CLOSE_DELAY)
  }

  function onRowEnter(id: string) {
    hoverRowRef.current = id
    clearCloseTimer()
    clearOpenTimer()
    if (activeRow === id) return
    openTimerRef.current = window.setTimeout(() => {
      if (hoverRowRef.current === id) setActiveRow(id)
    }, HOVER_OPEN_DELAY)
  }
  function onRowLeave(id: string) {
    if (hoverRowRef.current === id) hoverRowRef.current = null
    clearOpenTimer()
    scheduleClose()
  }

  function onRowClick(id: string) {
    hoverRowRef.current = id
    clearOpenTimer()
    clearCloseTimer()
    setActiveRow(id)
  }
  function onFlyoutEnter() {
    hoverFlyoutRef.current = true
    clearCloseTimer()
  }
  function onFlyoutLeave() {
    hoverFlyoutRef.current = false
    scheduleClose()
  }

  function closeAll() {
    setOpen(false)
    setActiveRow(null)
  }

  return (

    <div>
      <Menu
        open={open}
        onOpenChange={setOpen}
        dismissIgnore={[flyoutRef, FOREIGN_POPOVER_SELECTOR]}
        isList
        presentation="pill" retainTrigger
        trigger={({ triggerProps }) => (
          <Button
            {...triggerProps}
            iconOnly isActive={open || active}
            aria-label="Filter"
            title="Filter"
          >
            <Icon name="i-filter" />
          </Button>
        )}
      >
        {rows.map((row) => (
          <MenuRow
            key={row.id}
            ref={(el) => {
              rowElRef.current[row.id] = el
            }}
            label={row.label}
            icon={({ status: 'i-board', coach: 'i-person', kostentraeger: 'i-building', datum: 'i-calendar' } as Record<string, string>)[row.id] ?? 'i-filter'}
            count={row.kind === 'radio' ? undefined : row.count}
            isOpen={activeRow === row.id}
            onMouseEnter={() => onRowEnter(row.id)}
            onMouseLeave={() => onRowLeave(row.id)}
            onSelect={() => onRowClick(row.id)}
          />
        ))}
      </Menu>
      {open &&
        activeRowConfig &&
        createPortal(
          <MenuFlyout
            ref={flyoutRef}
            isOpen
            className={activeRowConfig.kind === 'custom' && activeRowConfig.stacked ? 'filter-menu-flyout--datum' : undefined}
            placement={flyoutPos}
            onMouseEnter={onFlyoutEnter}
            onMouseLeave={onFlyoutLeave}
          >
            {activeRowConfig.kind === 'radio' ? (
              <RadioOptionList options={activeRowConfig.options} current={activeRowConfig.current} onSelect={(value) => {
                activeRowConfig.onSelect(value)
                closeAll()
              }} />
            ) : activeRowConfig.kind === 'options' ? (
              <CheckboxOptionList options={activeRowConfig.options} excluded={activeRowConfig.excluded}
                onToggle={activeRowConfig.onToggle} sorted={activeRowConfig.sorted} />
            ) : activeRowConfig.render()}
          </MenuFlyout>,
          document.body,
        )}
    </div>
  )
}
